import { useRoleLabels } from "../lib/RoleLabelsContext";
import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";

export interface RunNotification {
  id: string;
  learner: { id: string; displayName: string };
  runId: string;
  title: string;
  score: number | null;
  maxScore: number | null;
  completedAt: string;
  readAt: string | null;
  createdAt: string;
}

export function groupRunNotifications(notifications: RunNotification[]) {
  const groups = new Map<string, { learner: RunNotification["learner"]; notifications: RunNotification[] }>();
  for (const notification of notifications) {
    const group = groups.get(notification.learner.id);
    if (group) group.notifications.push(notification);
    else groups.set(notification.learner.id, { learner: notification.learner, notifications: [notification] });
  }
  return [...groups.values()];
}

export function groupRunNotificationsByDate(notifications: RunNotification[]) {
  const days = new Map<string, { day: string; date: Date; notifications: RunNotification[] }>();
  const sorted = [...notifications].sort((a, b) => Date.parse(b.completedAt) - Date.parse(a.completedAt));
  for (const notification of sorted) {
    const date = new Date(notification.completedAt);
    const day = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    const group = days.get(day);
    if (group) group.notifications.push(notification);
    else days.set(day, { day, date, notifications: [notification] });
  }
  return [...days.values()].map(({ day, date, notifications: entries }) => ({ day, date, groups: groupRunNotifications(entries) }));
}

type NotificationGrouping = "learner" | "date";
const groupingStorageKey = "calepia:notification-grouping";

function storedGrouping(): NotificationGrouping {
  try { return localStorage.getItem(groupingStorageKey) === "date" ? "date" : "learner"; }
  catch { return "learner"; }
}

const dateOnly = new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" });
const dateTime = new Intl.DateTimeFormat("de-DE", {
  dateStyle: "medium",
  timeStyle: "short",
});

export function TherapistNotifications({ onOpenLearnerAnalysis }: { onOpenLearnerAnalysis: (learnerId: string, runId: string) => Promise<void> }) {
  const labels = useRoleLabels();
  const [notifications, setNotifications] = useState<RunNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [grouping, setGrouping] = useState<NotificationGrouping>(storedGrouping);
  const groups = useMemo(() => groupRunNotifications(notifications), [notifications]);
  const dateGroups = useMemo(() => groupRunNotificationsByDate(notifications), [notifications]);

  function changeGrouping(value: NotificationGrouping) {
    setGrouping(value);
    try { localStorage.setItem(groupingStorageKey, value); } catch { /* Switching also works without browser storage. */ }
  }

  useEffect(() => {
    let active = true;
    api<RunNotification[]>("/learning/run-notifications")
      .then((result) => { if (active) setNotifications(result); })
      .catch((error) => { if (active) setMessage(error.message); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  async function markRead(id: string) {
    setMessage("");
    const result = await api<{ id: string; readAt: string }>(`/learning/run-notifications/${encodeURIComponent(id)}/read`, { method: "PATCH" });
    setNotifications((current) => current.map((notification) => notification.id === id ? { ...notification, readAt: result.readAt } : notification));
  }

  async function remove(id: string) {
    setMessage("");
    await api(`/learning/run-notifications/${encodeURIComponent(id)}`, { method: "DELETE" });
    setNotifications((current) => current.filter((notification) => notification.id !== id));
  }

  function renderLearnerGroup(group: ReturnType<typeof groupRunNotifications>[number], day?: string) {
    const unread = group.notifications.filter(({ readAt }) => !readAt).length;
    const headingId = `learner-notifications-${day ?? "all"}-${group.learner.id}`;
    const Heading = day ? "h4" : "h3";
    return (
      <section className="notification-group" key={group.learner.id} aria-labelledby={headingId}>
        <header>
          <span className="notification-avatar" aria-hidden="true">{group.learner.displayName.slice(0, 1).toUpperCase()}</span>
          <div>
            <Heading id={headingId}>{group.learner.displayName}</Heading>
            <small>{group.notifications.length} {group.notifications.length === 1 ? "abgeschlossener Lauf" : "abgeschlossene Läufe"}</small>
          </div>
          {unread > 0 && <span className="notification-unread-count">{unread} neu</span>}
        </header>
        <div className="notification-list">
          {group.notifications.map((notification) => (
            <article className={`run-notification ${notification.readAt ? "read" : "unread"}`} key={notification.id}>
              <span className="notification-status-dot" aria-hidden="true" />
              <div className="notification-content">
                <div className="notification-title-line">
                  <strong>{notification.title}</strong>
                  {!notification.readAt && <span>Neu</span>}
                </div>
                <p>
                  Abgeschlossen am <time dateTime={notification.completedAt}>{dateTime.format(new Date(notification.completedAt))}</time>
                  {notification.score !== null && notification.maxScore !== null && <> · {notification.score} von {notification.maxScore} richtig</>}
                </p>
              </div>
              <div className="notification-actions">
                <button className="notification-history-button" onClick={() => onOpenLearnerAnalysis(notification.learner.id, notification.runId).catch((error) => setMessage(error.message))}>Zum Übungsverlauf</button>
                {!notification.readAt && <button className="notification-read-button" onClick={() => markRead(notification.id).catch((error) => setMessage(error.message))}>Als gelesen markieren</button>}
                <button className="notification-delete-button" onClick={() => remove(notification.id).catch((error) => setMessage(error.message))}>Löschen</button>
              </div>
            </article>
          ))}
        </div>
      </section>
    );
  }

  return (
    <section className="start-section notification-section" aria-labelledby="notifications-title">
      <div className="start-section-heading">
        <div>
          <p className="eyebrow">Neuigkeiten: {labels.LEARNER.plural}</p>
          <h2 id="notifications-title">Benachrichtigungen</h2>
        </div>
        {notifications.some(({ readAt }) => !readAt) && (
          <span className="notification-total">{notifications.filter(({ readAt }) => !readAt).length} ungelesen</span>
        )}
      </div>

      <div className="notification-grouping" role="group" aria-label="Benachrichtigungen gruppieren">
        <button type="button" aria-pressed={grouping === "learner"} onClick={() => changeGrouping("learner")}>{labels.LEARNER.plural}</button>
        <button type="button" aria-pressed={grouping === "date"} onClick={() => changeGrouping("date")}>Nach Datum</button>
      </div>

      {message && <div className="alert error">{message}</div>}
      {loading ? (
        <div className="notification-loading">Benachrichtigungen werden geladen …</div>
      ) : groups.length === 0 ? (
        <div className="notification-empty">
          <span aria-hidden="true">✓</span>
          <div><strong>Alles auf dem neuesten Stand</strong><small>Neue abgeschlossene Übungsläufe erscheinen automatisch hier.</small></div>
        </div>
      ) : (
        <div className="notification-groups">
          {grouping === "learner" ? groups.map((group) => renderLearnerGroup(group)) : dateGroups.map(({ day, date, groups: learners }) => (
            <section className="notification-date-group" key={day} aria-labelledby={`notification-day-${day}`}>
              <h3 id={`notification-day-${day}`}><time dateTime={day}>{dateOnly.format(date)}</time></h3>
              <div className="notification-groups">{learners.map((group) => renderLearnerGroup(group, day))}</div>
            </section>
          ))}
        </div>
      )}
    </section>
  );
}
